/**
 * Other error patterns (subscription, file upload, network, etc.)
 * Extracted from constants.ts to reduce file size
 */

import { MAX_SEND_BATCH_FILE_COUNT } from "@Front/Constants/uploadLimits";
import { PAYLOAD_TOO_LARGE_USER_MESSAGE } from "@Front/Utils/sanitizeApiErrorBodyForUser";
import { getUploadTextScope } from "@Front/Components/DesignSystem/helpers/fileUploadUiI18n";
import type { UserFriendlyError } from "../types";
import { workspaceDemonstrativeTarget, workspaceNounTitle, workspaceSubject } from "@Front/Utils/publicSiteBranding";

function capitalizeFirstLetter(s: string): string {
	return s.length > 0 ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : s;
}

const workspaceDemonstrative = workspaceDemonstrativeTarget();
const workspaceDemonstrativeTitle = capitalizeFirstLetter(workspaceDemonstrative);

/**
 * Subscription error patterns
 */
export const subscriptionErrorPatterns: Array<{
	pattern: RegExp;
	message: UserFriendlyError | ((match: RegExpMatchArray) => UserFriendlyError);
}> = [
	{
		pattern: /subscription.*expired|no.*active.*subscription/i,
		message: {
			title: "Abonnement inactif",
			message: "Votre abonnement a expiré ou n'est pas actif. Veuillez renouveler votre abonnement.",
			canRetry: false,
		},
	},
	{
		pattern: /unable to set active office.*not affiliated|unable to set active office.*active license/i,
		message: {
			title: `${workspaceNounTitle()} non disponible`,
			message: `${workspaceDemonstrativeTitle} n'est pas disponible. Vous n'êtes peut-être pas affilié à ${workspaceDemonstrative} ou ${workspaceSubject()} n'a pas de licence active.`,
			canRetry: false,
		},
	},
	{
		pattern: /office.*partagé.*dossiers|dossiers invités|GUEST_FOLDERS/i,
		message: {
			title: `${workspaceNounTitle()} avec dossiers partagés`,
			message: `${workspaceDemonstrativeTitle} a partagé des dossiers avec vous. Pour y accéder, veuillez utiliser l'option "Dossiers invités" au lieu de changer directement vers ${workspaceDemonstrative}.`,
			canRetry: false,
		},
	},
	{
		pattern: /seats.*exceeded|too many users/i,
		message: {
			title: "Limite atteinte",
			message: "Vous avez atteint la limite d'utilisateurs de votre abonnement. Contactez votre administrateur.",
			canRetry: false,
		},
	},
];

/**
 * File upload error patterns
 */
export const fileUploadErrorPatterns: Array<{
	pattern: RegExp;
	message: UserFriendlyError | ((match: RegExpMatchArray) => UserFriendlyError);
}> = [
	{
		pattern: /413|request entity too large/i,
		message: {
			title: getUploadTextScope("sendDocuments").tooLargeTitle,
			message: PAYLOAD_TOO_LARGE_USER_MESSAGE,
			canRetry: true,
		},
	},
	{
		pattern: /limit_unexpected_file|too many files|plus de \d+ fichiers/i,
		message: {
			title: getUploadTextScope("sendDocuments").tooManyTitle,
			message: `Vous ne pouvez pas envoyer plus de ${MAX_SEND_BATCH_FILE_COUNT} fichiers en une seule fois. Retirez des fichiers ou effectuez plusieurs envois.`,
			canRetry: true,
		},
	},
	{
		pattern: /file.*too large|size.*exceeded/i,
		message: {
			title: "Fichier trop volumineux",
			message: "Le fichier est trop volumineux. La taille maximale est de 100 Mo.",
			canRetry: true,
		},
	},
	{
		pattern: /invalid file type|unsupported.*format/i,
		message: {
			title: "Format de fichier non supporté",
			message: "Le format du fichier n'est pas accepté.",
			canRetry: true,
		},
	},
	{
		pattern: /format du fichier image.*n'est pas valide|fichier.*corrompu|unsupported image format|Image to PDF conversion failed/i,
		message: {
			title: "Format d'image invalide",
			message: "Le format du fichier image n'est pas valide ou le fichier est corrompu. Veuillez vérifier que le fichier est une image valide (PNG, JPEG, GIF, WebP) et réessayer.",
			canRetry: true,
		},
	},
];

/**
 * Titre du toast d'échec réseau (aucune réponse serveur : failed to fetch / connection reset).
 * Réutilisé par la corrélation requestId pour requalifier en erreur serveur quand une réponse existe.
 */
export const NETWORK_ERROR_TITLE = "Problème de connexion";

/**
 * Network error patterns (messages fonctionnels, non techniques)
 */
export const networkErrorPatterns: Array<{
	pattern: RegExp;
	message: UserFriendlyError | ((match: RegExpMatchArray) => UserFriendlyError);
}> = [
	{
		pattern: /network.*error|connection.*failed|failed to fetch|load failed|err_connection_reset|connection reset/i,
		message: {
			title: NETWORK_ERROR_TITLE,
			message:
				"Impossible de joindre le serveur : aucune réponse n'a été reçue. Vérifiez votre connexion internet ou un éventuel filtrage réseau (pare-feu, proxy de votre établissement), puis réessayez.",
			canRetry: true,
		},
	},
	{
		pattern: /timeout/i,
		message: {
			title: "Temps d'attente dépassé",
			message: "La requête a pris trop de temps. Veuillez réessayer.",
			canRetry: true,
		},
	},
];

/**
 * Prisma/DB specific error patterns
 */
export const databaseSpecificErrorPatterns: Array<{
	pattern: RegExp;
	message: UserFriendlyError | ((match: RegExpMatchArray) => UserFriendlyError);
}> = [
	{
		pattern: /unknown arg|unknown field/i,
		message: {
			title: "Erreur de données",
			message: "Certaines informations envoyées ne sont pas reconnues. Veuillez rafraîchir la page.",
			canRetry: true,
		},
	},
	{
		pattern: /provided list<json>/i,
		message: {
			title: "Format de données incorrect",
			message: "Les données envoyées sont dans un format incorrect. Veuillez réessayer.",
			canRetry: true,
		},
	},
];

/**
 * Anchoring error patterns
 */
export const anchoringErrorPatterns: Array<{
	pattern: RegExp;
	message: UserFriendlyError | ((match: RegExpMatchArray) => UserFriendlyError);
}> = [
	{
		pattern: /ancrage impossible.*aucun document|no documents found/i,
		message: {
			title: "Ancrage impossible",
			message: "Aucun document trouvé dans ce dossier.",
			canRetry: false,
		},
	},
	{
		pattern: /blockchain.*indisponible|bitcoin.*unavailable/i,
		message: {
			title: "Service d'ancrage indisponible",
			message: "Le service d'ancrage blockchain est temporairement indisponible. Veuillez réessayer plus tard.",
			canRetry: true,
		},
	},
	{
		pattern: /anchor.*failed|ancrage.*échoué/i,
		message: {
			title: "Erreur d'ancrage",
			message: "L'ancrage sur la blockchain a échoué. Veuillez réessayer ou contacter le support.",
			canRetry: true,
		},
	},
];

/**
 * Member removal business-rule patterns (client, third party, invited notary).
 * The removal guard throws a plain French sentence; without this mapping it falls through to the
 * generic "une erreur technique" fallback, hiding the real, actionable reason from the notary.
 */
export const memberRemovalErrorPatterns: Array<{
	pattern: RegExp;
	message: UserFriendlyError | ((match: RegExpMatchArray) => UserFriendlyError);
}> = [
	{
		pattern: /retirer ce membre.*documents.*liés.*participation/i,
		message: {
			title: "Retrait impossible",
			message:
				"Ce membre ne peut pas être retiré : un ou plusieurs documents sont liés à sa participation sur ce dossier.",
			canRetry: false,
		},
	},
	{
		pattern: /retirer ce membre.*messages.*fil de discussion/i,
		message: {
			title: "Retrait impossible",
			message:
				"Ce membre ne peut pas être retiré : des messages ont été échangés dans le fil de discussion avec lui sur ce dossier.",
			canRetry: false,
		},
	},
	{
		pattern: /retirer ce membre.*documents.*liés.*participation.*messages.*fil de discussion/i,
		message: {
			title: "Retrait impossible",
			message:
				"Ce membre ne peut pas être retiré : des documents sont liés à sa participation et des messages ont été échangés dans le fil de discussion sur ce dossier.",
			canRetry: false,
		},
	},
	{
		pattern: /retirer le membre.*documents.*validés|un ou plusieurs documents ont été validés/i,
		message: {
			title: "Retrait impossible",
			message: "Ce membre ne peut pas être retiré : un ou plusieurs documents sont liés à sa participation sur ce dossier.",
			canRetry: false,
		},
	},
];

/**
 * Third-party specific error patterns
 */
export const thirdPartyErrorPatterns: Array<{
	pattern: RegExp;
	message: UserFriendlyError | ((match: RegExpMatchArray) => UserFriendlyError);
}> = [
	{
		pattern: /third.*party.*already.*exists|tiers.*existe.*déjà/i,
		message: {
			title: "Tiers déjà présent",
			message: "Ce tiers est déjà associé à ce dossier.",
			canRetry: false,
		},
	},
	{
		pattern: /client sur ce dossier|déjà utilisé par un client sur ce dossier/i,
		message: {
			title: "Email réservé au client sur ce dossier",
			message:
				"Cette adresse est déjà celle d'un client rattaché à ce dossier. Retirez le client du dossier ou indiquez une autre adresse pour le tiers.",
			canRetry: false,
		},
	},
	{
		pattern: /email.*already.*used|email.*déjà.*utilisé/i,
		message: {
			title: "Email déjà utilisé",
			message: "Cette adresse email est déjà utilisée par un autre utilisateur.",
			canRetry: false,
		},
	},
	{
		pattern: /unexpected end of form|fin inattendue.*formulaire/i,
		message: {
			title: "Erreur lors de l'envoi du fichier",
			message: "Le fichier n'a pas pu être envoyé correctement. Veuillez vérifier votre connexion et réessayer.",
			canRetry: true,
		},
	},
];

